import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { FolderTree } from '../../components/knowledge-base/FolderTree';
import { DocumentList } from '../../components/knowledge-base/DocumentList';
import { DocumentEditor } from '../../components/knowledge-base/DocumentEditor';
import { FolderManager } from '../../components/knowledge-base/FolderManager';
import { SearchBar } from '../../components/knowledge-base/SearchBar';
import { FileUploader } from '../../components/knowledge-base/FileUploader';
import {
  knowledgeBaseFolderApi,
  knowledgeBaseDocumentApi,
  KnowledgeBaseFolder,
  KnowledgeBaseDocument,
} from '../../api/knowledge-base';
import {
  FolderIcon,
  FileTextIcon,
  UploadIcon,
  PlusIcon,
  BrainIcon,
} from 'lucide-react';

export default function KnowledgeBasePage() {
  const [folders, setFolders] = useState<KnowledgeBaseFolder[]>([]);
  const [documents, setDocuments] = useState<KnowledgeBaseDocument[]>([]);
  const [selectedFolder, setSelectedFolder] =
    useState<KnowledgeBaseFolder | null>(null);
  const [selectedDocument, setSelectedDocument] =
    useState<KnowledgeBaseDocument | null>(null);
  const [showDocumentEditor, setShowDocumentEditor] = useState(false);
  const [showFolderManager, setShowFolderManager] = useState(false);
  const [showFileUploader, setShowFileUploader] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<{
    visaTypes: string[];
    contentType?: string;
    tags: string[];
  }>({ visaTypes: [], tags: [] });
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    loadFolders();
    loadDocuments();
  }, []);

  useEffect(() => {
    if (selectedFolder) {
      loadDocuments(selectedFolder.id);
    } else if (
      searchQuery ||
      filters.visaTypes.length > 0 ||
      filters.tags.length > 0
    ) {
      searchDocuments();
    } else {
      loadDocuments();
    }
  }, [selectedFolder, searchQuery, filters]);

  const loadFolders = async () => {
    try {
      const data = await knowledgeBaseFolderApi.getTree();
      setFolders(data);
    } catch (error) {
      console.error('Failed to load folders:', error);
      setErrorMsg('Failed to load folders');
    }
  };

  const loadDocuments = async (folderId?: string) => {
    try {
      setLoading(true);
      const result = await knowledgeBaseDocumentApi.search({
        folderId,
        limit: 100,
      });
      setDocuments(result.documents);
      setErrorMsg(null);
    } catch (error) {
      console.error('Failed to load documents:', error);
      setErrorMsg('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const searchDocuments = async () => {
    try {
      setLoading(true);
      const result = await knowledgeBaseDocumentApi.search({
        query: searchQuery,
        visaTypes: filters.visaTypes.length > 0 ? filters.visaTypes : undefined,
        contentType: filters.contentType,
        tags: filters.tags.length > 0 ? filters.tags : undefined,
        limit: 100,
      });
      setDocuments(result.documents);
      setErrorMsg(null);
    } catch (error) {
      console.error('Failed to search documents:', error);
      setErrorMsg('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleFolderSelect = (folder: KnowledgeBaseFolder | null) => {
    setSelectedFolder(folder);
    setSearchQuery('');
  };

  const handleDocumentSelect = (document: KnowledgeBaseDocument) => {
    setSelectedDocument(document);
    setShowDocumentEditor(true);
  };

  const handleCreateDocument = () => {
    setSelectedDocument(null);
    setShowDocumentEditor(true);
  };

  const handleCreateFolder = () => {
    setShowFolderManager(true);
  };

  const handleDocumentSaved = async () => {
    setShowDocumentEditor(false);
    setSelectedDocument(null);
    await loadDocuments(selectedFolder?.id);
  };

  const handleFolderSaved = async () => {
    setShowFolderManager(false);
    await loadFolders();
  };

  const handleFileUploaded = async () => {
    setShowFileUploader(false);
    await loadDocuments(selectedFolder?.id);
  };

  const handleDocumentDeleted = async () => {
    await loadDocuments(selectedFolder?.id);
  };

  return (
    <Layout>
      <div className="flex flex-col bg-gray-50 -m-6 min-h-[calc(100vh-200px)]">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <BrainIcon className="w-8 h-8 text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Knowledge Base
                </h1>
                <p className="text-sm text-gray-500">
                  Templates, field definitions, and legal precedents
                </p>
                {/* Removed stats counters */}
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowFileUploader(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <UploadIcon className="w-4 h-4 mr-2" />
                Upload File
              </button>
              <button
                onClick={handleCreateFolder}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <FolderIcon className="w-4 h-4 mr-2" />
                New Folder
              </button>
              <button
                onClick={handleCreateDocument}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                New Document
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="mt-4">
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              filters={filters}
              onFiltersChange={setFilters}
              isSearching={loading && !!searchQuery}
              resultCount={searchQuery ? documents.length : undefined}
            />
            {errorMsg && (
              <div className="mt-3 bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded">
                {errorMsg}
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Folder Sidebar */}
          <div className="w-64 bg-white border-r border-gray-200 overflow-y-auto">
            <div className="p-4">
              <FolderTree
                folders={folders}
                selectedFolder={selectedFolder}
                onFolderSelect={handleFolderSelect}
              />
            </div>
          </div>

          {/* Document List */}
          <div className="flex-1 overflow-y-auto">
            <DocumentList
              documents={documents}
              loading={loading}
              onDocumentSelect={handleDocumentSelect}
              onDocumentDeleted={handleDocumentDeleted}
              searchQuery={searchQuery}
            />
          </div>
        </div>

        {/* Modals */}
        {showDocumentEditor && (
          <DocumentEditor
            document={selectedDocument}
            defaultFolderId={selectedFolder?.id}
            onClose={() => {
              setShowDocumentEditor(false);
              setSelectedDocument(null);
            }}
            onSaved={handleDocumentSaved}
          />
        )}

        {showFolderManager && (
          <FolderManager
            parentFolder={selectedFolder}
            onClose={() => setShowFolderManager(false)}
            onSaved={handleFolderSaved}
          />
        )}

        {showFileUploader && (
          <FileUploader
            defaultFolderId={selectedFolder?.id}
            onClose={() => setShowFileUploader(false)}
            onUploaded={handleFileUploaded}
          />
        )}
      </div>
    </Layout>
  );
}
